import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "pages/Rostering/UpdatedTimeSheet/TimeSheetDetailsSection/ai/utils/bodyScrollLock";
import "./vertex360Copilot.css";
import { VERTEX360_COPILOT_DRAWER_TRANSITION_MS, VERTEX360_COPILOT_DRAWER_Z_INDEX } from "./constants/vertex360CopilotConstants";
import { VERTEX360_COPILOT_THEME } from "./constants/vertex360CopilotTheme";
import { useVertex360CopilotChat } from "./hooks/useVertex360CopilotChat";
import {
  buildClarifyFollowUp,
  buildClarifyFollowUpFromAnswers,
  normalizeClarifyQuestions,
  pickClarifySelectedOption,
  sanitizeClarifyAnswers,
} from "./utils/vertex360CopilotChatUtils";
import Vertex360CopilotHeader from "./components/Vertex360CopilotHeader";
import ChatMessageList from "./components/ChatMessageList";
import MessageComposer from "./components/MessageComposer";
import { classNames } from "Utils/utils";

const Vertex360CopilotDrawer = ({ open, onClose, onOpenFullscreen }) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [composerFocusKey, setComposerFocusKey] = useState(0);

  const {
    messages,
    hasConversation,
    conversationId,
    isAssistantTyping,
    isStreaming,
    streamingMessageId,
    isLoadingOlder,
    hasMoreOlder,
    scrollAnchorRef,
    sendMessage,
    selectClarifyOption,
    rateMessageFeedback,
    clearActiveFilters,
    activeFilters,
    contextSummary,
    loadOlderMessages,
    resetConversation,
    stopGeneration,
    retryMessage,
  } = useVertex360CopilotChat();

  useBodyScrollLock(mounted, {
    nestedScrollAllowSelector: "[data-scroll-lock-allow]",
  });

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }

    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), VERTEX360_COPILOT_DRAWER_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (isStreaming || isAssistantTyping) {
          stopGeneration();
        } else {
          onClose?.();
        }
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, onClose, isStreaming, isAssistantTyping, stopGeneration]);

  const handleNewChat = () => {
    resetConversation();
    setComposerFocusKey((key) => key + 1);
  };

  const handleClarifyOption = (prompt, option, message) => {
    if (option?.answers && option?.questions) {
      const questions = normalizeClarifyQuestions(option.questions);
      const answers = sanitizeClarifyAnswers(option.answers, questions);
      const optionId = pickClarifySelectedOption(questions, answers);
      const typedEntry = Object.values(answers).find(
        (ans) => ans && typeof ans === "object" && String(ans.text || "").trim()
      );
      const typed = typedEntry ? String(typedEntry.text).trim() : "";
      const displayLabel = typed;
      let clarify;
      const serverMessageId = selectClarifyOption(
        message,
        optionId || "__typed__",
        displayLabel || undefined,
        answers
      );
      // Always send chip/answers — even before complete rekeys server id —
      // so BE can merge filters without relying on clarify_message_id alone.
      clarify = {
        ...(serverMessageId ? { clarifyMessageId: serverMessageId } : {}),
        clarifySelectedOption: optionId || "__typed__",
        ...(displayLabel ? { clarifySelectedLabel: displayLabel } : {}),
        clarifyAnswers: answers,
      };
      const followUp = buildClarifyFollowUpFromAnswers(prompt, questions, answers);
      if (followUp.trim()) sendMessage(followUp, clarify);
      return;
    }
    const typed = String(option?.typedLabel || "").trim();
    const optionId = String(option?.id || "").trim();
    const optionLabel = String(option?.label || "").trim();
    const displayLabel = typed || optionLabel;
    // API bind uses opaque id (pt:/ep:); chat copy uses the human label.
    const choice = optionId || displayLabel;
    let clarify;
    if (message && optionId) {
      const serverMessageId = selectClarifyOption(
        message,
        optionId,
        displayLabel || undefined
      );
      clarify = {
        ...(serverMessageId ? { clarifyMessageId: serverMessageId } : {}),
        clarifySelectedOption: optionId,
        ...(displayLabel ? { clarifySelectedLabel: displayLabel } : {}),
      };
    }
    const followUp = buildClarifyFollowUp(prompt, choice, displayLabel);
    if (followUp.trim()) sendMessage(followUp, clarify);
  };

  const isBusy = isAssistantTyping || isStreaming;

  if (!mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex justify-end"
      style={{ zIndex: VERTEX360_COPILOT_DRAWER_Z_INDEX }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vertex360-copilot-drawer-title"
      data-scroll-lock-allow
    >
      <button
        type="button"
        className={classNames(
          "absolute inset-0 bg-slate-900/50 backdrop-blur-[3px] transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
        aria-label="Close Vertex360 Copilot"
      />

      <aside
        className={classNames(
          "relative flex h-full w-full flex-col shadow-2xl transition-transform duration-300",
          "max-w-[420px] sm:max-w-[520px] lg:max-w-[580px]",
          VERTEX360_COPILOT_THEME.drawerBg,
          VERTEX360_COPILOT_THEME.drawerBorder,
          "border-l",
          visible ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden="true"
        >
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#4B6CB7]/8 blur-3xl" />
          <div className="absolute -bottom-16 left-0 h-48 w-48 rounded-full bg-[#293F6A]/6 blur-3xl" />
        </div>

        <Vertex360CopilotHeader
          onClose={onClose}
          onNewChat={handleNewChat}
          onOpenFullscreen={onOpenFullscreen}
          showNewChat={hasConversation}
        />

        <ChatMessageList
          messages={messages}
          hasConversation={hasConversation}
          conversationId={conversationId}
          isAssistantTyping={isAssistantTyping}
          isStreaming={isStreaming}
          streamingMessageId={streamingMessageId}
          isLoadingOlder={isLoadingOlder}
          hasMoreOlder={hasMoreOlder}
          onLoadOlder={loadOlderMessages}
          onSelectPrompt={sendMessage}
          onRetryMessage={retryMessage}
          onClarifyOption={handleClarifyOption}
          onMessageRated={rateMessageFeedback}
          scrollContainerRef={scrollAnchorRef}
          drawerVisible={visible}
        />

        <MessageComposer
          key={`drawer-composer-${composerFocusKey}`}
          onSend={sendMessage}
          onStop={stopGeneration}
          disabled={false}
          isSending={isBusy}
          focusRequestKey={composerFocusKey}
          activeFilters={activeFilters}
          contextSummary={contextSummary}
          onClearFilters={clearActiveFilters}
        />
      </aside>
    </div>,
    document.body
  );
};

export default Vertex360CopilotDrawer;
